import { fetchData } from '../../fetchapi/fetchapi.js';

// User-centric, CROSS-PROJECT view of the caller's own app/agent requests.
// The backend GET /api/my_app_requests returns ALL of the caller's requests
// across every project (never project-scoped), newest first, each queued row
// carrying a computed queue_position, plus open_count (queued/claimed/running).
export const fetchMyRequests = async (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== '')
  ).toString();
  return fetchData(`my_app_requests${qs ? '?' + qs : ''}`);
};

// Cancel a still-queued (or stale in-flight) request (requester-only server-side).
export const cancelMyRequest = async (id) =>
  fetchData(`app_requests/${id}`, { method: 'DELETE' });

// Dismiss (remove from the "Board") a TERMINAL request. Requester-only,
// terminal-status-only server-side; the row stays in the DB as audit.
export const dismissMyRequest = async (id) =>
  fetchData(`app_requests/${id}/dismiss`, { method: 'POST' });

// Dismiss ALL of the caller's terminal requests at once ("Clear history").
export const dismissMyTerminalRequests = async () =>
  fetchData(`my_app_requests/dismiss_terminal`, { method: 'POST' });

// Signed GET URL for one of a request's stored attachments (requester or
// worker only, server-side). Returns { url, name, size, content_type }.
export const requestAttachmentUrl = async (id, index = 0) =>
  fetchData(`app_requests/${id}/attachment_url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ index }),
  });

// ── Task graph edges (app_request_link) ──────────────────────────────────────
// Separate from the parent link above: arbitrary typed, directed edges between
// cards, so the board carries the interlinked structure the Docs vault gets
// from [[wikilinks]]. Not requester-scoped — a dependency between two people's
// cards is expressible.

export const LINK_KINDS = ['relates', 'blocks', 'supersedes', 'duplicates'];

// Both directions for one task: { id, outgoing: [...], incoming: [...] },
// each entry { link_id, kind, created_at, task: {id,title,status,kind,project_slug} }.
export const fetchRequestLinks = async (id) => fetchData(`app_requests/${id}/links`);

// Create one edge id -> to. Idempotent server-side (re-linking returns
// created: false rather than erroring).
export const linkRequest = async (id, to, kind = 'relates') =>
  fetchData(`app_requests/${id}/links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to, kind }),
  });

// Remove one edge touching this task — either direction, so a backlink chip
// can be dropped from the card you are looking at.
export const unlinkRequest = async (id, linkId) =>
  fetchData(`app_requests/${id}/links/${linkId}`, { method: 'DELETE' });

// Attach a task to another task of the same requester (parent link), or
// detach with parent_id: null. Informational grouping only — execution is
// unaffected. Self-links and cycles are rejected server-side.
export const setRequestParent = async (id, parentId) =>
  fetchData(`app_requests/${id}/parent`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ parent_id: parentId }),
  });

// Board drag-and-drop: move a request between the user-controlled statuses.
// `from` rides along for optimistic concurrency — the backend re-checks it in
// the UPDATE's WHERE, so a card the worker drain claimed mid-drag 409s instead
// of being yanked back. Allowed moves: todo ⇄ queued, failed|cancelled → queued.
export const moveRequest = async (id, from, to) =>
  fetchData(`app_requests/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ from, to }),
  });

// Edit a parked task's brief (title/details/kind/target). Server-side only
// To Do / Failed / Cancelled rows accept edits (409 once a worker owns it).
export const editRequest = async (id, patch) =>
  fetchData(`app_requests/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });

// The card's comment thread (feedback loop): people and agents both write
// here. Visibility mirrors the card; comments are allowed on ANY status.
export const fetchRequestComments = async (id) =>
  fetchData(`app_requests/${id}/comments`);

// One full card: unlike the board list (whose per-card log is capped to the
// last 20 entries), this carries the COMPLETE Activity trail + comments —
// what the detail dialog needs to tell a long run's story from line one.
export const fetchRequest = async (id) =>
  fetchData(`app_requests/${id}`);

export const addRequestComment = async (id, body) =>
  fetchData(`app_requests/${id}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });

// Session transcripts — the FULL Claude Code transcripts of the fleet sessions
// that worked a card, kept on S3 forever (backend/modules/apps: transcripts).
export const fetchRequestTranscripts = async (id) => fetchData(`app_requests/${id}/transcripts`);
export const requestTranscriptUrl = async (tid) =>
  fetchData(`app_requests/transcripts/${tid}/url`, { method: 'POST' });
